/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Card, Accordion, useAccordionToggle } from 'react-bootstrap';

import { FormSelect, FormNumberInput, FormCheckbox, FormSelectNumberInput } from '@components/Common/AwpFormInputs';
import { CardHeaderToggle } from '@components/Common/Accordion/CardHeaderToggle';

export const OccurrenceOptionalFields = ({ props, eventProperties, readOnly, useMargin, isAccordion = true }) => {
  const [activeKey, setActiveKey] = useState(null);

  const avalancheSections = [...new Set(eventProperties.map((z) => z.SectionDescription))].filter(
    (x) => (x || '').length > 0,
  );

  useEffect(() => {
    if (props.values?.aval_mass_toe_dist && props.values.aval_mass_toe_dist > 0) {
      setActiveKey('impact_to_road');
    } else {
      setActiveKey(null);
    }
  }, [props.values?.aval_mass_toe_dist]);

  const handleManualToggle = (eventKey) => {
    // Toggle the Accordion manually based on user interaction
    if (activeKey === eventKey) {
      setActiveKey(null); // Close the panel if it's already open
    } else {
      setActiveKey(eventKey); // Open the clicked panel
    }
  };

  const sectionItemsWithSelect = (item, i) => {
    const options = [...item.ObservationValues]
      .filter((x) => x.EndTimestamp == null)
      .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
      .map((value) => {
        let label = `${value.ObservedCharacterValue}`;
        if (value.DisplayedMeaning) {
          label += ` (${value.DisplayedMeaning})`;
        }
        return {
          label: label,
          value: value.ObservedCharacterValue,
        };
      });

    return (
      <div key={`${item.Id}-${i}`}>
        <FormSelect
          readOnly={readOnly}
          id={`${item.ObservationName}`}
          name={`${item.ObservationName}`}
          label={item.DisplayName}
          options={options}
          isMulti={false}
          useMargin={useMargin}
          isClearable={true}
        />
        <>
          {item.IsMeasured.toUpperCase() === 'Y' ? (
            <FormCheckbox
              readOnly={readOnly}
              id={`${item.ObservationName}_measured`}
              name={`${item.ObservationName}_measured`}
              useMargin={useMargin}
              label="Measured"
              value="false"
            />
          ) : null}
        </>
      </div>
    );
  };

  //assumes numeric input
  //TODO: handle DataType to spit out String or Number field
  const sectionItemsWithInput = (item, i) => {
    const fieldLabel = item.DisplayName + (item.UnitAbbreviation.length > 0 ? ` [${item.UnitAbbreviation}]` : '');

    return (
      <div key={`${item.Id}-${i}`}>
        <FormNumberInput
          id={item.ObservationName}
          name={item.ObservationName}
          label={fieldLabel}
          readOnly={readOnly}
          useMargin={useMargin}
        />
        <>
          {item.IsMeasured.toUpperCase() === 'Y' ? (
            <FormCheckbox
              readOnly={readOnly}
              id={`${item.ObservationName}_measured`}
              name={`${item.ObservationName}_measured`}
              useMargin={useMargin}
              label="Measured"
              value="false"
            />
          ) : null}
        </>
      </div>
    );
  };

  const sectionItemsWithSelectNumberInput = (item, i) => {
    const fieldLabel = item.DisplayName + (item.UnitAbbreviation.length > 0 ? ` [${item.UnitAbbreviation}]` : '');
    const displayFormat = item.DisplayFormat;
    let instruction = '';
    if (displayFormat) {
      const decimalPlaces = displayFormat.startsWith('F') ? displayFormat.substring(1, 2) : 0;
      const multiplier = Math.pow(10, decimalPlaces);
      const incrementStr = displayFormat.substring(2) === '' ? 1 : displayFormat.substring(2);
      const increment = (displayFormat.startsWith('F') ? incrementStr : 0) / multiplier || 1;
      instruction = displayFormat.startsWith('F')
        ? `Range: ${item.LowerLimit} - ${item.UpperLimit}, Decimal places: ${decimalPlaces}, Increment: ${increment}`
        : '';
    }
    const options = [...item.ObservationValues]
      .filter((x) => x.EndTimestamp == null)
      .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
      .map((value) => {
        let label = `${value.ObservedCharacterValue}`;
        if (value.DisplayedMeaning) {
          label += ` (${value.DisplayedMeaning})`;
        }
        return {
          label: label,
          value: value.ObservedCharacterValue,
        };
      });

    return (
      <div key={`${item.Id}-${i}`}>
        <FormSelectNumberInput
          readOnly={readOnly}
          id={`${item.ObservationName}`}
          name={`${item.ObservationName}`}
          label={fieldLabel}
          options={options}
          useMargin={useMargin}
          isMulti={false}
          isClearable={true}
          instruction={instruction}
        />
        <>
          {item.IsMeasured.toUpperCase() === 'Y' ? (
            <FormCheckbox
              readOnly={readOnly}
              useMargin={useMargin}
              id={`${item.ObservationName}_measured`}
              name={`${item.ObservationName}_measured`}
              label="Measured"
              value="false"
            />
          ) : null}
        </>
      </div>
    );
  };

  const buildSections = (avalancheSections) => {
    return avalancheSections.map((section, i) => {
      const sectionItems = eventProperties.filter((e) => e.SectionDescription === section);
      const sectionId = section.toLowerCase().replace(/ /g, '_');

      return isAccordion ? (
        <Card key={sectionId} border="light" className="page-card">
          <CardHeaderToggle
            eventKey={sectionId}
            as="h5"
            title={section}
            onClick={() => handleManualToggle(sectionId)}
            isRequired={sectionId === 'impact_to_road' && props.values['aval_mass_toe_dist'] > 0}
          />
          <Accordion.Collapse eventKey={sectionId}>
            <Card.Body>
              <>
                {sectionItems.map((item, i) => {
                  return item.ObservationValues.length > 0
                    ? item.DataType === 'Numeric'
                      ? sectionItemsWithSelectNumberInput(item, i)
                      : sectionItemsWithSelect(item, i)
                    : sectionItemsWithInput(item, i);
                })}
              </>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      ) : (
        <div key={sectionId}>
          {sectionItems.map((item, i) => {
            return item.ObservationValues.length > 0
              ? item.DataType === 'Numeric'
                ? sectionItemsWithSelectNumberInput(item, i)
                : sectionItemsWithSelect(item, i)
              : sectionItemsWithInput(item, i);
          })}
        </div>
      );
    });
  };
  return isAccordion ? (
    <Accordion activeKey={activeKey}>{buildSections(avalancheSections)}</Accordion>
  ) : (
    <>{buildSections(avalancheSections)}</>
  );
};
